import { useMemo } from 'react';
import type { FigureComposition } from '@/store/figure-composition';
import { mmToPx } from '@/lib/applyJournalTemplate';

interface RulersProps {
  composition: FigureComposition;
  zoom: number;
  containerWidth: number;
  containerHeight: number;
  previewAtJournalSize?: boolean;
}

export function Rulers({ composition, zoom, containerWidth, containerHeight, previewAtJournalSize = false }: RulersProps) {
  const mmToPxScaled = (mm: number): number => {
    return (mm / 25.4) * 96 * zoom;
  };

  // Journal limit dimensions (from journal template or default)
  const journalLimitWidthMm = composition.canvasSize.width;
  const journalLimitHeightMm = composition.canvasSize.height;
  const journalLimitWidthPx = mmToPxScaled(journalLimitWidthMm);
  const journalLimitHeightPx = mmToPxScaled(journalLimitHeightMm);
  
  // When previewAtJournalSize is true, constrain rulers to canvas size only
  const rulerWidthPx = previewAtJournalSize ? journalLimitWidthPx : undefined;
  const rulerHeightPx = previewAtJournalSize ? journalLimitHeightPx : undefined;

  // Calculate visual canvas size (grows with panels, minimum = journal limit)
  const visualCanvasSize = useMemo(() => {
    // Start with journal limit as minimum
    let visualWidthMm = journalLimitWidthMm;
    let visualHeightMm = journalLimitHeightMm;
    
    // Calculate column width based on journal limit (for consistent grid)
    const journalLimitWidthPxUnscaled = mmToPx(journalLimitWidthMm);
    const colWidthPx = journalLimitWidthPxUnscaled / composition.grid.cols;
    const rowHeightPx = composition.grid.rowHeight;
    const marginX = composition.grid.margin[0];
    const marginY = composition.grid.margin[1];
    
    // Calculate max extent of panels
    composition.panels.forEach(panel => {
      // Panel right edge: x + w columns, accounting for margins
      const panelRightPx = (panel.x + panel.w) * colWidthPx + (panel.w - 1) * marginX;
      // Panel bottom edge: y + h rows, accounting for margins
      const panelBottomPx = (panel.y + panel.h) * rowHeightPx + (panel.h - 1) * marginY;
      
      // Convert back to mm
      const panelRightMm = (panelRightPx / 96) * 25.4;
      const panelBottomMm = (panelBottomPx / 96) * 25.4;
      
      visualWidthMm = Math.max(visualWidthMm, panelRightMm);
      visualHeightMm = Math.max(visualHeightMm, panelBottomMm);
    });
    
    return {
      widthMm: visualWidthMm,
      heightMm: visualHeightMm,
      journalLimitWidthMm,
      journalLimitHeightMm,
    };
  }, [composition.panels, journalLimitWidthMm, journalLimitHeightMm, composition.grid.cols, composition.grid.rowHeight, composition.grid.margin]);

  // Calculate tick marks (every 10mm) - show ticks up to journal limit (print size)
  const tickInterval = 10; // mm
  // When previewAtJournalSize is true, only calculate ticks up to journal limit
  // Otherwise, calculate based on visual canvas size
  const maxTickX = previewAtJournalSize ? journalLimitWidthMm : visualCanvasSize.widthMm;
  const maxTickY = previewAtJournalSize ? journalLimitHeightMm : visualCanvasSize.heightMm;
  const tickCountX = Math.ceil(maxTickX / tickInterval);
  const tickCountY = Math.ceil(maxTickY / tickInterval);

  const hasJournalTemplate = !!composition.theme?.journalTemplate && composition.theme.journalTemplate !== 'none';
  
  const ticksX = useMemo(() => {
    return Array.from({ length: tickCountX + 1 }, (_, i) => {
      const position = i * tickInterval;
      // Skip ticks beyond the maximum when in preview mode
      if (previewAtJournalSize && position > maxTickX + 0.1) {
        return null;
      }
      return {
        position,
        px: mmToPxScaled(position),
        isJournalLimit: hasJournalTemplate && Math.abs(position - journalLimitWidthMm) < 0.1, // Within 0.1mm
      };
    }).filter((tick): tick is NonNullable<typeof tick> => tick !== null);
  }, [tickCountX, zoom, journalLimitWidthMm, hasJournalTemplate, previewAtJournalSize, maxTickX]);

  const ticksY = useMemo(() => {
    return Array.from({ length: tickCountY + 1 }, (_, i) => {
      const position = i * tickInterval;
      // Skip ticks beyond the maximum when in preview mode
      if (previewAtJournalSize && position > maxTickY + 0.1) {
        return null;
      }
      return {
        position,
        px: mmToPxScaled(position),
        isJournalLimit: hasJournalTemplate && Math.abs(position - journalLimitHeightMm) < 0.1, // Within 0.1mm
      };
    }).filter((tick): tick is NonNullable<typeof tick> => tick !== null);
  }, [tickCountY, zoom, journalLimitHeightMm, hasJournalTemplate, previewAtJournalSize, maxTickY]);

  // Journal limit marker positions
  const journalLimitXPx = mmToPxScaled(journalLimitWidthMm);
  const journalLimitYPx = mmToPxScaled(journalLimitHeightMm);
  
  // FigureCanvas has padding of 16px (p-4), so rulers need to align with grid content
  const canvasPadding = 16;
  
  return (
    <div 
      className="absolute pointer-events-none z-40"
      style={{
        top: `${canvasPadding}px`,
        left: `${canvasPadding}px`,
        width: rulerWidthPx !== undefined ? `${rulerWidthPx}px` : `calc(100% - ${canvasPadding * 2}px)`,
        height: rulerHeightPx !== undefined ? `${rulerHeightPx}px` : `calc(100% - ${canvasPadding * 2}px)`,
      }}
    >
      {/* Journal limit vertical line - only show when journal template is selected */}
      {hasJournalTemplate && (
        <div
          className="absolute top-0 border-l-2 border-dashed border-red-500 opacity-70 z-50"
          style={{ 
            left: `${journalLimitXPx}px`,
            height: rulerHeightPx !== undefined ? `${rulerHeightPx}px` : '100%',
          }}
        >
          <div
            className="absolute top-0 left-0 bg-red-500 text-white text-[9px] px-1 py-0.5 rounded-br whitespace-nowrap font-semibold"
            style={{ transform: 'translateX(-50%)' }}
          >
            Journal Limit ({journalLimitWidthMm}mm)
          </div>
        </div>
      )}

      {/* Journal limit horizontal line - only show when journal template is selected */}
      {hasJournalTemplate && (
        <div
          className="absolute left-0 border-t-2 border-dashed border-red-500 opacity-70 z-50"
          style={{ 
            top: `${journalLimitYPx}px`,
            width: rulerWidthPx !== undefined ? `${rulerWidthPx}px` : '100%',
          }}
        >
          <div
            className="absolute top-0 left-0 bg-red-500 text-white text-[9px] px-1 py-0.5 rounded-br whitespace-nowrap font-semibold"
            style={{ transform: 'translateY(-100%)' }}
          >
            Journal Limit ({journalLimitHeightMm}mm)
          </div>
        </div>
      )}

      {/* Horizontal ruler (top) */}
      <div 
        className="absolute top-0 left-0 h-6 bg-gray-200/80 border-b border-gray-300 flex items-end"
        style={{ width: rulerWidthPx !== undefined ? `${rulerWidthPx}px` : '100%' }}
      >
        {ticksX.map((tick, i) => (
          <div
            key={`x-${i}`}
            className="absolute flex flex-col items-center"
            style={{ left: `${tick.px}px` }}
          >
            <div className={`w-px ${tick.isJournalLimit ? 'h-4 bg-red-500' : 'h-2 bg-gray-600'}`} />
            <span className={`text-[10px] mt-0.5 whitespace-nowrap ${tick.isJournalLimit ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
              {tick.position}mm{tick.isJournalLimit ? ' (limit)' : ''}
            </span>
          </div>
        ))}
      </div>

      {/* Vertical ruler (left) */}
      <div 
        className="absolute top-0 left-0 w-6 bg-gray-200/80 border-r border-gray-300 flex items-end flex-col"
        style={{ height: rulerHeightPx !== undefined ? `${rulerHeightPx}px` : '100%' }}
      >
        {ticksY.map((tick, i) => (
          <div
            key={`y-${i}`}
            className="absolute flex flex-row items-center"
            style={{ top: `${tick.px}px` }}
          >
            <div className={`h-px ${tick.isJournalLimit ? 'w-4 bg-red-500' : 'w-2 bg-gray-600'}`} />
            <span className={`text-[10px] ml-0.5 whitespace-nowrap ${tick.isJournalLimit ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
              {tick.position}mm{tick.isJournalLimit ? ' (limit)' : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

